import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { db } from 'firebase/firebase';
import { collection, getDocs, limit, onSnapshot, query, startAfter } from 'firebase/firestore';

import getDocData from '../../firebase/getDocData';
import {
  subcollectionPaginationBatchSuccess,
  subcollectionPaginationFirstBatch,
  subcollectionPaginationFirstBatchError,
  subcollectionPaginationNextBatch,
  subcollectionPaginationNextBatchError,
  subcollectionPaginationReset,
} from './actions';
import { getInitialState, reducer } from './reducer';

const DEFAULT_PAGE_SIZE = 10;

/**
 * Split a raw Firestore batch fetched with limit(pageSize + 1) into the docs we
 * actually display plus a definitive "hasMore" flag.
 *
 * We over-fetch by one document (the "peek") so we can tell whether more data
 * exists WITHOUT guessing from the batch size. Without the peek, a batch that
 * returns exactly `pageSize` docs is ambiguous (could be the last page or not),
 * which makes speculative-pagination render a phantom extra page at every exact
 * multiple of pageSize (e.g. 10 items / 5 per page showed 3 pages instead of 2).
 *
 * The cursor is the last DISPLAYED doc, so the next startAfter() resumes at the
 * peeked doc and nothing is skipped.
 */
export const splitPeekedBatch = (docs = [], pageSize) => {
  const hasMore = docs.length > pageSize;
  const displayDocs = hasMore ? docs.slice(0, pageSize) : docs;
  const lastDoc = displayDocs[displayDocs.length - 1];
  return { displayDocs, hasMore, lastDoc };
};

/**
 * Scalable hook for paginating collections with optional subcollections
 * COMPLETELY DECOUPLED from UI - can be used with any pagination approach!
 * 
 * @param {Object} collectionQuery - Firestore query for the main collection
 * @param {string|string[]} subcollectionNames - Name(s) of subcollection(s) to load (optional)
 * @param {number} pageSize - Page size (default: 10)
 * @param {Array} dependencies - Dependencies to re-execute the hook
 * @param {Object} options - Additional options
 * @param {boolean} options.loadSubcollectionsLazy - If true, subcollections are loaded on demand
 * @param {boolean} options.realtime - If true, enables onSnapshot for the first page and its subcollections
 *                                      (default false, so existing consumers keep getDocs behavior)
 * @returns {Object} Data, loading states, and functions - no UI coupling!
 * 
 * @example
 * // Basic usage
 * const { data, loading, next } = useCollectionWithSubcollectionsPaginated(query, 'policies', 10);
 * 
 * // With Ant Design Table native pagination
 * <Table dataSource={data} pagination={{ pageSize: 10, showSizeChanger: true }} />
 * 
 * // With infinite scroll  
 * const loadMore = () => next();
 * 
 * // With custom pagination
 * const { paginatedData } = usePaginationWithFirestore(data, { next, loading }, 10);
 * 
 * // Multiple subcollections
 * const result = useCollectionWithSubcollectionsPaginated(query, ['policies', 'metrics', 'notes']);
 */
const useCollectionWithSubcollectionsPaginated = (
  collectionQuery,
  subcollectionNames = [],
  pageSize = DEFAULT_PAGE_SIZE,
  dependencies = [],
  options = {}
) => {
  const {
    loadSubcollectionsLazy = false,
    realtime = false,
  } = options;

  const [state, dispatch] = useReducer(reducer, getInitialState());
  const [subcollectionCache, setSubcollectionCache] = useState({});
  const stateRef = useRef();
  const lastQueryRef = useRef();
  const unsubscribesRef = useRef([]);
  const subcollectionUnsubscribesRef = useRef({});
  
  stateRef.current = state;

  // Normalize subcollectionNames to array
  const normalizedSubcollectionNames = Array.isArray(subcollectionNames) 
    ? subcollectionNames 
    : [subcollectionNames].filter(Boolean);

  /**
   * Load subcollections for a specific document
   */
  const loadSubcollectionsForDoc = useCallback(async (docData, docPath) => {
    const subcollectionData = {};
    
    await Promise.all(
      normalizedSubcollectionNames.map(async (subcollectionName) => {
        try {
          const subcollectionRef = collection(db, docPath, subcollectionName);
          const subcollectionSnap = await getDocs(subcollectionRef);
          
          if (!subcollectionSnap.empty) {
            subcollectionData[subcollectionName] = subcollectionSnap.docs.map(subDoc => ({
              id: subDoc.id,
              ...subDoc.data()
            }));
          }
        } catch (error) {
          console.warn(`No subcollection ${subcollectionName} for doc ${docData.id}:`, error);
          subcollectionData[subcollectionName] = [];
        }
      })
    );

    return subcollectionData;
  }, [normalizedSubcollectionNames]);

  /**
   * Process a batch of documents loading their subcollections
   */
  const processBatchWithSubcollections = useCallback(async (docs, isFirstBatch = false) => {
    const processedDocs = {};
    
    await Promise.all(
      Object.entries(docs).map(async ([docId, docData]) => {
        const docPath = `${collectionQuery.path}/${docId}`;
        
        if (!loadSubcollectionsLazy && normalizedSubcollectionNames.length > 0) {
          // Load subcollections immediately
          const subcollectionData = await loadSubcollectionsForDoc(docData, docPath);
          processedDocs[docId] = {
            ...docData,
            ...subcollectionData
          };
        } else {
          // Only add flags for available subcollections
          processedDocs[docId] = {
            ...docData,
            _hasSubcollections: normalizedSubcollectionNames.length > 0,
            _subcollectionNames: normalizedSubcollectionNames
          };
        }
      })
    );

    return processedDocs;
  }, [collectionQuery.path, loadSubcollectionsLazy, normalizedSubcollectionNames, loadSubcollectionsForDoc]);

  /**
   * Load specific subcollection on demand (for lazy mode)
   */
  const loadSubcollection = useCallback(async (docId, subcollectionName) => {
    const cacheKey = `${docId}_${subcollectionName}`;
    
    if (subcollectionCache[cacheKey]) {
      return subcollectionCache[cacheKey];
    }

    try {
      const docPath = `${collectionQuery.path}/${docId}`;
      const subcollectionRef = collection(db, docPath, subcollectionName);
      const subcollectionSnap = await getDocs(subcollectionRef);
      
      const subcollectionData = subcollectionSnap.docs.map(subDoc => ({
        id: subDoc.id,
        ...subDoc.data()
      }));

      setSubcollectionCache(prev => ({
        ...prev,
        [cacheKey]: subcollectionData
      }));

      return subcollectionData;
    } catch (error) {
      console.error(`Error loading subcollection ${subcollectionName} for doc ${docId}:`, error);
      return [];
    }
  }, [collectionQuery.path, subcollectionCache]);

  const clearSubcollectionSubscriptions = useCallback(() => {
    Object.values(subcollectionUnsubscribesRef.current).forEach(docUnsubscribes => {
      Object.values(docUnsubscribes).forEach(unsubscribe => unsubscribe?.());
    });
    subcollectionUnsubscribesRef.current = {};
  }, []);

  const subscribeSubcollectionsForDoc = useCallback((docData, lastDoc) => {
    if (loadSubcollectionsLazy || normalizedSubcollectionNames.length === 0) return;

    const docPath = `${collectionQuery.path}/${docData.id}`;

    if (!subcollectionUnsubscribesRef.current[docData.id]) {
      subcollectionUnsubscribesRef.current[docData.id] = {};
    }

    normalizedSubcollectionNames.forEach(subcollectionName => {
      const currentDocSubscriptions = subcollectionUnsubscribesRef.current[docData.id];
      if (currentDocSubscriptions[subcollectionName]) return;

      const subcollectionRef = collection(db, docPath, subcollectionName);
      currentDocSubscriptions[subcollectionName] = onSnapshot(subcollectionRef, subcollectionSnap => {
        const subcollectionData = subcollectionSnap.docs.map(subDoc => ({
          id: subDoc.id,
          ...subDoc.data(),
        }));

        const currentDocData = stateRef.current.data[docData.id] || docData;
        const updatedDoc = {
          ...currentDocData,
          [subcollectionName]: subcollectionData,
        };

        dispatch(subcollectionPaginationBatchSuccess(lastDoc, { [docData.id]: updatedDoc }));
      });
    });
  }, [
    collectionQuery.path,
    dispatch,
    loadSubcollectionsLazy,
    normalizedSubcollectionNames,
  ]);

  // Callbacks for error and success handling
  const onNextBatchError = useCallback(
    error => dispatch(subcollectionPaginationNextBatchError(error)),
    [dispatch]
  );
  
  const onNextBatchSuccess = useCallback(
    (lastDocument, data, hasMore) =>
      dispatch(subcollectionPaginationBatchSuccess(lastDocument, data, undefined, hasMore)),
    [dispatch]
  );

  /**
   * Get next batch of data
   */
  const getNextBatch = useCallback(async () => {
    if (!stateRef.current.lastDocument || !stateRef.current.next || stateRef.current.loading) {
      return;
    }

    try {
      dispatch(subcollectionPaginationNextBatch());

      // Peek one extra doc (pageSize + 1) so we know for certain whether more
      // data exists beyond this page. See splitPeekedBatch.
      const nextBatch = query(
        lastQueryRef.current,
        startAfter(stateRef.current.lastDocument),
        limit(pageSize + 1)
      );

      const data = await getDocs(nextBatch);
      const { displayDocs, hasMore, lastDoc } = splitPeekedBatch(data.docs, pageSize);

      const docs = {};
      displayDocs.forEach(doc => {
        const docData = getDocData(doc);
        docs[docData.id] = docData;
      });

      // Process with subcollections
      const processedDocs = await processBatchWithSubcollections(docs);

      onNextBatchSuccess(lastDoc, processedDocs, hasMore);
    } catch (error) {
      onNextBatchError(error);
    }
  }, [dispatch, onNextBatchError, onNextBatchSuccess, pageSize, processBatchWithSubcollections]);

  // Callbacks for first batch
  const onFirstBatchError = useCallback(
    error => dispatch(subcollectionPaginationFirstBatchError(error)),
    [dispatch]
  );
  
  const onFirstBatchSuccess = useCallback(
    (lastDocument, data, hasMore) =>
      dispatch(subcollectionPaginationBatchSuccess(lastDocument, data, getNextBatch, hasMore)),
    [dispatch, getNextBatch]
  );

  /**
   * Get first batch of data
   */
  const getFirstBatch = useCallback(async () => {
    dispatch(subcollectionPaginationFirstBatch());
    
    try {
      // Peek one extra doc (pageSize + 1) so gotNewData reflects whether more
      // data really exists beyond the first page. See splitPeekedBatch.
      lastQueryRef.current = query(collectionQuery, limit(pageSize + 1));
      if (!realtime) {
        const data = await getDocs(lastQueryRef.current);
        const { displayDocs, hasMore, lastDoc } = splitPeekedBatch(data.docs, pageSize);

        const docs = {};
        displayDocs.forEach(doc => {
          const docData = getDocData(doc);
          docs[docData.id] = docData;
        });

        // Process with subcollections
        const processedDocs = await processBatchWithSubcollections(docs, true);

        onFirstBatchSuccess(lastDoc, processedDocs, hasMore);
        return;
      }

      const unsubscribe = onSnapshot(
        lastQueryRef.current,
        async snapshot => {
          const { displayDocs, hasMore, lastDoc } = splitPeekedBatch(snapshot.docs, pageSize);
          const docs = {};
          displayDocs.forEach(doc => {
            const docData = getDocData(doc);
            docs[docData.id] = docData;
          });

          const currentDocIds = new Set(Object.keys(docs));
          Object.entries(subcollectionUnsubscribesRef.current).forEach(([docId, docUnsubscribes]) => {
            if (currentDocIds.has(docId)) return;
            Object.values(docUnsubscribes).forEach(unsubscribeDoc => unsubscribeDoc?.());
            delete subcollectionUnsubscribesRef.current[docId];
          });

          const processedDocs = await processBatchWithSubcollections(docs, true);
          onFirstBatchSuccess(lastDoc, processedDocs, hasMore);
          Object.values(docs).forEach(docData => subscribeSubcollectionsForDoc(docData, lastDoc));
        },
        onFirstBatchError,
      );

      unsubscribesRef.current.push(unsubscribe);
    } catch (error) {
      onFirstBatchError(error);
    }
  }, [
    collectionQuery,
    onFirstBatchError,
    onFirstBatchSuccess,
    pageSize,
    processBatchWithSubcollections,
    realtime,
    subscribeSubcollectionsForDoc,
  ]);

  // Effects for lifecycle handling
  useEffect(() => {
    dispatch(subcollectionPaginationReset());
    setSubcollectionCache({});
    
    // Clean previous subscriptions
    unsubscribesRef.current.forEach(unsubscribe => unsubscribe());
    unsubscribesRef.current = [];
    clearSubcollectionSubscriptions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionQuery.path, ...dependencies]);

  useEffect(() => {
    if (!collectionQuery) return;
    
    if (!stateRef.current.loaded && !stateRef.current.loading) {
      getFirstBatch();
    }
  }, [collectionQuery, getFirstBatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unsubscribesRef.current.forEach(unsubscribe => unsubscribe());
      clearSubcollectionSubscriptions();
    };
  }, [clearSubcollectionSubscriptions]);

  return {
    ...stateRef.current,
    data: Object.values(stateRef.current.data),
    loadSubcollection, // For lazy mode
    subcollectionCache, // Cache of loaded subcollections
    // Debug information
    debugInfo: {
      subcollectionNames: normalizedSubcollectionNames,
      loadSubcollectionsLazy,
      realtime,
      cacheKeys: Object.keys(subcollectionCache)
    }
  };
};

export default useCollectionWithSubcollectionsPaginated;